import "./owner-table.scss";
import { HTMLProps, useCallback, useMemo } from 'react';
import useIsMobile from "../../../../../hooks/use-is-mobile";
import { SolicitorTypeEnum } from "../../../../../models/enum/solicitor-type.enum";
import { useAppSelector } from "../../../../../redux/hook";
import { HoldingTypeTypeCode, LoanTypeCode } from "../../../../../utils/code-converter";
import { solicitorTypeItems } from "../../../../../utils/shared-dropdown";
import { Helpers } from "../../../../../utils/helper";

type Item = "owners" | "occupiers" | "loan" | "holdingType" | "lawyer";
type solicitorType = "current" | "proposed";

export interface OwnerTableProps extends HTMLProps<HTMLDivElement> {
  itemsToShow?: Item[];
}

export function OwnerTable({ itemsToShow, ...props }: OwnerTableProps) {

  const applicationStart = useAppSelector(state => state.app.ApplicationStartSlice.formData);
  const applicationStep1 = useAppSelector(state => state.app.ApplicationStep1Slice.formData);
  const applicationStep2 = useAppSelector(state => state.app.ApplicationStep2Slice.formData);
  const applicationStep3 = useAppSelector(state => state.app.ApplicationStep3Slice.formData);

  const { isMobile } = useIsMobile();

  const currentOwners = useMemo(() => {
    return applicationStep1.household.owners.map(e => e.myinfo.name );
  }, [applicationStep1.household.owners])
  
  const proposedOwners = useMemo(() => {
    return applicationStep1.household.incoming_owners.map(e => e.myinfo.name );
  }, [applicationStep1.household.incoming_owners])
  
  const currentOccupiers = useMemo(() => {
    return applicationStep1.household.occupiers.map(e => e.myinfo.name );
  }, [applicationStep1.household.occupiers])
  
  const proposedOccupiers = useMemo(() => {
    return applicationStep1.household.incoming_occupiers.map(e => e.myinfo.name );
  }, [applicationStep1.household.incoming_occupiers])


  const currentLoan = useMemo(()=>{
    return LoanTypeCode.find(e => e.code === applicationStep2.current_loan_type)?.description;
  },[applicationStep2.current_loan_type])
  
  const proposedLoan = useMemo(()=>{
    return LoanTypeCode.find(e => e.code === applicationStep2.new_loan_type)?.description;
  },[applicationStep2.new_loan_type])
  
  const currentHoldingType = useMemo(()=>{
    return HoldingTypeTypeCode.find( e => e.code === applicationStart.selected_hdb_property?.holding_type)?.description;
  },[applicationStart.selected_hdb_property?.holding_type])
  
  const proposedHoldingType = useMemo(()=>{
    return HoldingTypeTypeCode.find( e => e.code === applicationStep3?.holding_type)?.description;
  },[applicationStep3?.holding_type])



  const ConveyancingRowUnderSolicitorColumn = useCallback((type: solicitorType, enableMasking:boolean = false)=>{
    
    let solicitorTypeValue = applicationStep3[`${type}_solicitor_type`]
    let currentProposeValue = applicationStep3[type];
    
    if(solicitorTypeValue === SolicitorTypeEnum.conveyancing_hdb_solicitor && applicationStep3[type].hdb_solicitor.is_confirm){
      return <span>{Helpers.maskContent(`HDB (Conveyancing)`, enableMasking)}</span>
    }
    else{
      //normal behaviour
      return <span>{Helpers.maskContent(`${currentProposeValue.conveyancing_private_solicitor.name_of_firm} (Conveyancing)`, enableMasking)}</span>
    }
   
  },[])

  const solicitorColumn = useCallback((type: solicitorType, enableMasking:boolean = false)=>{
    
    let selection_type = type === "current" ? applicationStep3.current_solicitor_type : applicationStep3.proposed_solicitor_type;
    let postfix_title = type === "current" ? "(Discharge of current loan)" : "(New mortgage loan)"
    let typeValue = applicationStep3[type];  

    if(selection_type && ((selection_type === SolicitorTypeEnum.hdb_solicitor && typeValue.hdb_solicitor.is_confirm) || (selection_type === SolicitorTypeEnum.private_solicitor && typeValue.private_solicitor.is_confirm))){
      
      const solicitor_type = selection_type === SolicitorTypeEnum.hdb_solicitor ? typeValue.hdb_solicitor : typeValue.private_solicitor;
      const sameRepresentation = solicitor_type.represented_same_solicitor;

      const primary_selection = selection_type === SolicitorTypeEnum.hdb_solicitor ? solicitorTypeItems.find(e => e.value === selection_type)?.title : typeValue.private_solicitor.name_of_firm;

      if(sameRepresentation){
        return <div>
            <span>{Helpers.maskContent(`${primary_selection}`, enableMasking)}</span>
          </div>
      }
      else{
        return <>
          <span>{Helpers.maskContent(`${primary_selection} ${postfix_title}`, enableMasking)}</span>
          {!enableMasking && ConveyancingRowUnderSolicitorColumn(type, enableMasking)}
        </>
      }

    }
    else if(selection_type && ((selection_type === SolicitorTypeEnum.conveyancing_hdb_solicitor && typeValue.hdb_solicitor.is_confirm) || (selection_type === SolicitorTypeEnum.conveyancing_private_solicitor && typeValue.conveyancing_private_solicitor.is_confirm))){
      return <>
        {ConveyancingRowUnderSolicitorColumn(type, enableMasking)}
      </>
    }
    else{
      return <p className='ohc-household-structure-table-placeholder'>You have not selected any lawyer to represent the {type} owners</p>
    }
   
  },[ConveyancingRowUnderSolicitorColumn, applicationStep3])

  return isMobile ? (
    <div {...props}>
      {/* Current table */}
      <div className="polymer-owner-table">
        <div className="polymer-owner-table__row header" />
        <div className="polymer-owner-table__row header">Current</div>

        {/* Owners */}
        {!itemsToShow || itemsToShow.includes("owners") ? (
          <>
            <div className="polymer-owner-table__row index">Owners</div>
            <div className="polymer-owner-table__row">
              {currentOwners.map((owner) => (
                <span>{owner}</span>
              ))}
              {currentOwners.length === 0 && <span>-</span>}
            </div>
          </>
        ) : null}

        {/* Occupiers */}
        {!itemsToShow || itemsToShow.includes("occupiers") ? (
          <>
            <div className="polymer-owner-table__row index">Occupiers</div>
            <div className="polymer-owner-table__row">
              {currentOccupiers.map((occupier) => (
                <span>{occupier}</span>
              ))}
              {currentOccupiers.length === 0 && <span>-</span>}
            </div>
          </>
        ) : null}

        {/* Loan */}
        {!itemsToShow || itemsToShow.includes("loan") ? (
          <>
            <div className="polymer-owner-table__row index">Loan</div>
            <div className="polymer-owner-table__row">{currentLoan}</div>
          </>
        ) : null}

        {/* Holding type */}
        {!itemsToShow || itemsToShow.includes("holdingType") ? (
          <>
            <div className="polymer-owner-table__row index">Holding Type</div>
            <div className="polymer-owner-table__row">{currentHoldingType}</div>
          </>
        ) : null}

        {/* Lawyer */}
        {!itemsToShow || itemsToShow.includes("lawyer") ? (
          <>
            <div className="polymer-owner-table__row index hide-border">Lawyer</div>
            <div className="polymer-owner-table__row hide-border">{solicitorColumn('current')}</div>
          </>
        ) : null}
      </div>

      {/* Proposed table */}
      <div className="polymer-owner-table">
        <div className="polymer-owner-table__row header" />
        <div className="polymer-owner-table__row header">Proposed</div>

        {/* Owners */}
        {!itemsToShow || itemsToShow.includes("owners") ? (
          <>
            <div className="polymer-owner-table__row index">Owners</div>
            <div className="polymer-owner-table__row">
              {proposedOwners.map((owner) => (
                <span>{owner}</span>
              ))}
              {proposedOwners.length === 0 && <span>-</span>}
            </div>
          </>
        ) : null}

        {/* Occupiers */}
        {!itemsToShow || itemsToShow.includes("occupiers") ? (
          <>
            <div className="polymer-owner-table__row index">Occupiers</div>
            <div className="polymer-owner-table__row">
              {proposedOccupiers.map((occupier) => (
                <span>{occupier}</span>
              ))}
              {proposedOccupiers.length === 0 && <span>-</span>}
            </div>
          </>
        ) : null}

        {/* Loan */}
        {!itemsToShow || itemsToShow.includes("loan") ? (
          <>
            <div className="polymer-owner-table__row index">Loan</div>
            <div className="polymer-owner-table__row">{proposedLoan}</div>
          </>
        ) : null}

        {/* Holding type */}
        {!itemsToShow || itemsToShow.includes("holdingType") ? (
          <>
            <div className="polymer-owner-table__row index">Holding Type</div>
            <div className="polymer-owner-table__row">{proposedHoldingType}</div>
          </>
        ) : null}

        {/* Lawyer */}
        {!itemsToShow || itemsToShow.includes("lawyer") ? (
          <>
            <div className="polymer-owner-table__row index hide-border">Lawyer</div>
            <div className="polymer-owner-table__row hide-border">{solicitorColumn('proposed')}</div>
          </>
        ) : null}
      </div>
    </div>
  ) : (
    <div className="polymer-owner-table" {...props}>
      {/* Header */}
      <div className="polymer-owner-table__row header" />
      <div className="polymer-owner-table__row header">Current</div>
      <div className="polymer-owner-table__row header">Proposed</div>

      {/* Owners */}
      {!itemsToShow || itemsToShow.includes("owners") ? (
        <>
          <div className="polymer-owner-table__row index">Owners</div>
          <div className="polymer-owner-table__row">
            {currentOwners.map((owner) => (
              <span>{owner}</span>
            ))}
          </div>
          <div className="polymer-owner-table__row">
            {proposedOwners.map((owner) => (
              <span>{owner}</span>
            ))}
          </div>
        </>
      ) : null}

      {/* Occupiers */}
      {!itemsToShow || itemsToShow.includes("occupiers") ? (
        <>
          <div className="polymer-owner-table__row index">Occupiers</div>
          <div className="polymer-owner-table__row">
            {currentOccupiers.map((occupier) => (
              <span>{occupier}</span>
            ))}
            {currentOccupiers.length === 0 && <span>-</span>}
          </div>
          <div className="polymer-owner-table__row">
            {proposedOccupiers.map((occupier) => (
              <span>{occupier}</span>
            ))}
            {proposedOccupiers.length === 0 && <span>-</span>}
          </div>
        </>
      ) : null}

      {/* Loan */}
      {!itemsToShow || itemsToShow.includes("loan") ? (
        <>
          <div className="polymer-owner-table__row index">Loan</div>
          <div className="polymer-owner-table__row">{currentLoan}</div>
          <div className="polymer-owner-table__row">{proposedLoan}</div>
        </>
      ) : null}

      {/* Holding type */}
      {!itemsToShow || itemsToShow.includes("holdingType") ? (
        <>
          <div className="polymer-owner-table__row index">Holding Type</div>
          <div className="polymer-owner-table__row">{currentHoldingType}</div>
          <div className="polymer-owner-table__row">{proposedHoldingType}</div>
        </>
      ) : null}

      {/* Lawyer */}
      {!itemsToShow || itemsToShow.includes("lawyer") ? (
        <>
          <div className="polymer-owner-table__row index hide-border">Lawyer</div>
          <div className="polymer-owner-table__row hide-border">{solicitorColumn("current")}</div>
          <div className="polymer-owner-table__row hide-border">{solicitorColumn("proposed")}</div>
        </>
      ) : null}
    </div>
  );
}
